import React from 'react';
import { DataTableTypes } from '../../DataTableTypes';
import { internalColumnType } from '../const/internalColumnType';
import { ExpandRowCell } from '../ui/row/cells/ExpandRowCell';
import { booleanOrFunctionToBoolean, MenuCell } from '../ui/row/cells/MenuCell';
import { CellContent } from '../ui/row/cells/CellContent';
import { ColumnDef, type ColumnMeta, createColumnHelper } from '@tanstack/react-table';
import { SelectRowCell } from '../ui/row/cells/SelectRowCell';
import { dataTableClassnames } from '../const/classNames';
import { SelectRowHeadCell } from '../ui/row/cells/SelectRowHeadCell';
import { filterNotEmpty } from '../../../../utils/listHelpers';
import { SortCell } from '../ui/row/cells/SortCell';

const CELL_MIN_WIDTH = 100;

export function toDataTableColumns<DATA_MODEL extends DataTableTypes.DataModel>(
  columns: DataTableTypes.ColumnDefinition<any>[],
  derivedState: DataTableTypes.DerivedState<any>
): ColumnDef<DATA_MODEL, DATA_MODEL>[] {
  return filterNotEmpty([
    derivedState.features.selectable.enabled ? ({ type: internalColumnType.selectRow } as const) : undefined,
    derivedState.features.expandable.enabled ? ({ type: internalColumnType.expandRow } as const) : undefined,
    ...columns
  ]).map((column) => toDataTableColumn(column, derivedState) as ColumnDef<DATA_MODEL, DATA_MODEL>);
}

function toDataTableColumn(
  column:
    | DataTableTypes.ColumnDefinition<any>
    | { type: internalColumnType.expandRow }
    | { type: internalColumnType.selectRow },
  options: DataTableTypes.DerivedState<any>
): ColumnDef<any> {
  const columnHelper = createColumnHelper<DataTableTypes.DataModel>();
  if (!column.type || column.type === 'column') {
    const maxSize = column.visibility?.maxWidth;
    const minSize = column.visibility?.minWidth ?? CELL_MIN_WIDTH;
    const size = (column.visibility?.width ?? 0) < minSize ? minSize : column.visibility?.width;

    return columnHelper.accessor(column.id as any, {
      cell: ({ row }) => {
        return (
          <CellContent
            dataTest={toDataTest(column, row.original)}
            style={column.visibility?.cellStyles}
            before={column.CellBeforeContent?.(row.original, row) ?? undefined}
            after={column.CellAfterContent?.(row.original, row) ?? undefined}
            align={column.visibility?.align}
          >
            {column.Cell(row.original, {
              depth: row.depth
            })}
          </CellContent>
        );
      },
      meta: {
        getCellWrapperStyle: column.visibility?.cellWrapperStyle
      } as ColumnMeta<object, unknown>,
      enableResizing: options?.features.columnManagement.resizable.enabled,
      header: () => {
        return (
          <CellContent
            dataTest={toDataTest(column)}
            style={column.visibility?.cellStyles}
            align={column.visibility?.align}
          >
            {!column.Header ? '' : column.Header}
            {!!column.sort && (
              <SortCell type={column.sort} slotProps={{ iconButton: { sx: { marginInlineStart: 2 } } }} />
            )}
          </CellContent>
        );
      },
      maxSize,
      minSize,
      size,
      enableHiding: !column.visibility?.blockColumnHiding
    });
  }

  if (column.type === internalColumnType.selectRow) {
    return columnHelper.display({
      id: internalColumnType.selectRow,
      enableHiding: false,
      enableGrouping: false,
      enableSorting: false,
      enableResizing: false,
      size: 50,
      minSize: 50,
      maxSize: 50,
      header: ({ table }) => {
        return (
          <SelectRowHeadCell
            onToggle={table.toggleAllPageRowsSelected}
            checkedSome={table.getIsSomePageRowsSelected()}
            checkedAll={table.getIsAllPageRowsSelected()}
          />
        );
      },
      cell: ({ row }) => {
        return (
          <SelectRowCell
            onToggle={row.toggleSelected}
            checked={row.getIsSelected()}
            dataTest={dataTableClassnames.table.cell.field(internalColumnType.selectRow)}
          />
        );
      }
    });
  }
  if (column.type === internalColumnType.expandRow) {
    return columnHelper.display({
      id: internalColumnType.expandRow,
      enableHiding: false,
      enableGrouping: false,
      enableSorting: false,
      enableResizing: false,
      size: 72,
      minSize: 72,
      maxSize: 72,
      cell: ({ row }) => {
        if (!row.getCanExpand()) {
          return null;
        }
        return (
          <ExpandRowCell
            onToggle={row.toggleExpanded}
            checked={row.getIsExpanded()}
            dataTest={dataTableClassnames.table.cell.field(internalColumnType.expandRow)}
          />
        );
      }
    });
  }
  if (column.type === internalColumnType.menu) {
    return columnHelper.display({
      id: internalColumnType.menu,
      enableHiding: false,
      enableGrouping: false,
      enableSorting: false,
      enableResizing: false,
      size: 50,
      minSize: 50,
      maxSize: 50,
      meta: {
        getCellWrapperStyle: () => ({
          marginLeft: 'auto'
        })
      } as ColumnMeta<object, unknown>,
      cell: ({ row }) => {
        if (booleanOrFunctionToBoolean(column.hidden, row.original)) {
          return null;
        }
        return (
          <MenuCell
            actions={column.actions}
            data={row.original}
            dataTest={toDataTest(column, row.original)}
            style={column.visibility?.cellStyles}
          />
        );
      }
    });
  }
  return { id: 'unknown', cell: () => 'Unknown column type' };
}

function toDataTest(
  column: Pick<DataTableTypes.ColumnDefinition<any>, 'dataTest'> & { id?: string },
  value?: unknown
): string | undefined {
  return !column.dataTest
    ? dataTableClassnames.table.cell.field('id' in column && column.id ? column.id : 'menu')
    : typeof column.dataTest === 'string'
      ? column.dataTest
      : !!value
        ? column.dataTest(value)
        : undefined;
}
